import { useRef, useState } from "react";
import type { FC } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
	Avatar,
	Box,
	Button,
	ButtonBase,
	Divider,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Popover,
	Typography,
} from "@mui/material";
import useAuth from "../../hooks/useAuth";
import CogIcon from "../../icons/Cog";
import Lockr from "lockr";
import { StorageKeys } from "../../utils/storageKeys";
import { useTranslation } from "react-i18next";
import { version } from "src/config";

const AccountPopover: FC = () => {
	const { t } = useTranslation();
	const anchorRef = useRef<HTMLButtonElement | null>(null);
	const { user, logout } = useAuth();
	const navigate = useNavigate();
	const [open, setOpen] = useState<boolean>(false);

	const handleOpen = (): void => {
		setOpen(true);
	};

	const handleClose = (): void => {
		setOpen(false);
	};

	const handleLogout = async (): Promise<void> => {
		try {
			handleClose();
			await logout();
			navigate("/");
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<>
			<Box
				component={ButtonBase}
				onClick={handleOpen}
				ref={anchorRef}
				data-testid="account-popover-avatar"
				sx={{
					alignItems: "center",
					display: "flex",
				}}
			>
				<Avatar
					src={"/static/mock-images/avatars/DefaultAvatar.svg"}
					sx={{
						height: 40,
						width: 40,
					}}
				/>
			</Box>
			<Popover
				anchorEl={anchorRef.current}
				anchorOrigin={{
					horizontal: "center",
					vertical: "bottom",
				}}
				keepMounted
				onClose={handleClose}
				open={open}
				PaperProps={{
					sx: { width: 310 },
				}}
			>
				<Box sx={{ p: 2 }}>
					<Typography color="textPrimary" variant="subtitle2">
						{user.name}
					</Typography>
					<Typography color="textSecondary" variant="subtitle2">
						{Lockr.get(StorageKeys.TenantName)}
					</Typography>
					<Typography color="textSecondary" variant="subtitle2">
						{t("Version")}: {version}
					</Typography>
					<Typography color="textSecondary" variant="subtitle2">
						{Lockr.get(StorageKeys.TenantId)}
					</Typography>
				</Box>
				<Divider />
				<Box sx={{ mt: 2 }} display="none">
					<MenuItem component={RouterLink} to="/dashboard/account">
						<ListItemIcon>
							<CogIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText
							primary={
								<Typography
									color="textPrimary"
									variant="subtitle2"
								>
									Settings
								</Typography>
							}
						/>
					</MenuItem>
				</Box>
				<Box sx={{ p: 2 }}>
					<Button
						color="primary"
						fullWidth
						onClick={handleLogout}
						variant="outlined"
						data-testid="logout-button"
					>
						{t("Logout")}
					</Button>
				</Box>
			</Popover>
		</>
	);
};

export default AccountPopover;
